<script setup lang="ts">
import {computed, nextTick, ref} from "vue";
import {useTextareaAutosize} from "@vueuse/core";
import {
    ArrowLeftRight,
    Check,
    CirclePlus,
    LoaderCircle,
    MessageSquarePlus,
    MessageSquareText,
    Pencil,
    ShieldAlert,
    Sparkles,
    Trash2
} from "@lucide/vue";
import {Input} from "@/core/components/ui/input";
import {Textarea} from "@/core/components/ui/textarea";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import BaseProductSearchBar from "@/product/components/BaseProductSearchBar.vue";
import {LegacyProduct} from "@/core/interfaces/Product";
import {Alert} from "@/medication-checkup/interfaces/alerts";
import {namesProduct, requiresOutcome} from "@/medication-checkup/services/InterventionStatuses";
import {
    InterventionAddition,
    InterventionDraft,
    InterventionFinding,
    InterventionFindingType,
    InterventionProduct,
    InterventionRow,
    InterventionStatus,
    InterventionStatusOption
} from "@/medication-checkup/interfaces/interventions";
import {CARD_SHEET, CHIP, STATUS_CHIPS} from "@/medication-checkup/services/SceneClasses";
import {alertLevelTone} from "@/medication-checkup/services/AlertLevelTones";
import MedicationCheckupAiBadge from "@/medication-checkup/components/MedicationCheckupAiBadge.vue";

/**
 * The card of the pharmaceutical intervention: the before / after table of
 * the analysis — one row per retained treatment across four columns: the
 * treatment with its posology and its findings as chips, the analysis, the
 * status, the actions — and, under a row, what unfolds from it: the
 * proposal being written, the comment. The treatments to add close the
 * table.
 *
 * The three families of findings share the treatment column as chips rather
 * than three columns of their own: they are seldom filled, and the width
 * they cost is width the words of the analysis need.
 *
 * Every word comes served: the rows, the statuses, the alerts. Drafting is
 * the scene's state, handed to the card while live; the frozen card reads
 * the interventions the record holds. The analysis is served too, and the
 * pencil of its column lets the pharmacist rewrite it — the rewriting lives
 * in the draft, so the words served stay readable behind it. The alerts of
 * a treatment are the analysis' matter, not a panel of their own: the words
 * say what they are worth, and the profile alerts alone keep a banner. The
 * card owns the colours and what is folded or unfolded, nothing else.
 */

const props = defineProps<{
    rows: InterventionRow[];
    profileAlerts: Alert[];
    statuses: InterventionStatusOption[];
    frozen?: boolean;
    /**
     * What the pharmacist drafted on each row, by row key, while live.
     */
    drafts?: Record<string, InterventionDraft>;
    additions?: InterventionAddition[];
    busy?: boolean;
    /**
     * Whether the AI draft of the table is being awaited.
     */
    drafting?: boolean;
}>();

const emit = defineEmits<{
    'update:draft': [key: string, draft: InterventionDraft];
    add: [product: InterventionProduct, outcome: string];
    'update:addition': [addition: InterventionAddition];
    'remove-addition': [key: string];
}>();

/**
 * The columns of the table, header and rows alike.
 *
 * The analysis takes the widest share: it carries sentences where the
 * treatment carries a name and a posology, and the status a label of its
 * own — both sized to what their longest word needs, no more.
 */
const GRID = 'grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)_132px_52px] gap-3 px-4 border-t border-gray-light';

const FINDING_CHIPS: Record<InterventionFindingType, string> = {
    adherence: 'bg-orange/25 text-gray-dark',
    side_effect: 'bg-red-light/15 text-red-light',
    galenic: 'bg-lightPurple-lightest text-primary'
};

/**
 * The colour of the title of an alert, on the scale of Vidal — the tone of
 * an information for a code off the scale.
 */
/**
 * The families of findings, in the order the chips read.
 */
const FAMILIES: { type: InterventionFindingType }[] = [
    {type: 'adherence'},
    {type: 'side_effect'},
    {type: 'galenic'}
];

const SOURCE_TITLES: Record<InterventionFinding['source'], string> = {
    probe: "Rapporté par le patient à l'entretien",
    girerd: 'Questionnaire de Girerd'
};

const SUBROW = 'col-span-full flex items-center gap-1.5';

const SUBINPUT = 'flex-1 min-w-0 h-7 !border-solid !border-gray bg-white px-2 py-1 text-[11.5px] shadow-none';

/**
 * The row whose proposal is being written, if any.
 */
const editing = ref<string | null>(null);

/**
 * The row whose analysis is being rewritten, if any. One at a time, so the
 * rewriting shares a single field and a single measure of its height.
 */
const rewriting = ref<string | null>(null);

/**
 * The field the rewriting opens, sized to the words it holds: an analysis
 * of a few lines is read whole, without a scrollbar inside a row of the
 * table.
 */
const analysisField = ref<HTMLTextAreaElement>();

const {triggerResize} = useTextareaAutosize({element: analysisField});

/**
 * Hold the field itself rather than the component wrapping it: the measure
 * is taken on the textarea.
 */
const bindAnalysisField = (instance: unknown) => {
    analysisField.value = (instance as {$el?: HTMLTextAreaElement} | null)?.$el;
};

/**
 * The rows whose comment is unfolded, beyond the ones that carry one.
 */
const commenting = ref<string[]>([]);

const addition = ref<{ product: InterventionProduct | null; outcome: string }>({product: null, outcome: ''});

/**
 * Remounting the search bar is how it forgets a selection.
 */
const searchGeneration = ref(0);

const additions = computed(() => props.additions ?? []);

const draftOf = (row: InterventionRow): InterventionDraft => {
    return props.drafts?.[row.key] ?? {status: 'unchanged', product: null, outcome: '', comment: '', analysis: null};
};

/**
 * What a row reads as: the draft while live, the record once frozen.
 */
const statusOf = (row: InterventionRow): InterventionStatus => {
    return props.frozen ? (row.intervention?.status ?? 'unchanged') : draftOf(row).status;
};

const outcomeOf = (row: InterventionRow): string => {
    return props.frozen ? (row.intervention?.outcome ?? '') : draftOf(row).outcome;
};

const commentOf = (row: InterventionRow): string => {
    return props.frozen ? (row.intervention?.comment ?? '') : draftOf(row).comment;
};

const productOf = (row: InterventionRow): InterventionProduct | null => {
    return props.frozen ? (row.intervention?.product ?? null) : draftOf(row).product;
};

/**
 * What the analysis column reads: the words the pharmacist rewrote where
 * they rewrote them, the words served otherwise.
 */
const analysisOf = (row: InterventionRow): string => {
    if (props.frozen) {
        return row.analysis ?? '';
    }

    return draftOf(row).analysis ?? row.analysis ?? '';
};

const isRewriting = (row: InterventionRow): boolean => !props.frozen && rewriting.value === row.key;

/**
 * Open the analysis of a row to rewrite it. The words served are the ones
 * it opens on: rewriting corrects the analysis, it does not start from a
 * blank line.
 */
const rewrite = (row: InterventionRow) => {
    update(row, {analysis: analysisOf(row)});
    rewriting.value = row.key;

    nextTick(triggerResize);
};

const closeRewrite = (row: InterventionRow) => {
    if (rewriting.value === row.key) {
        rewriting.value = null;
    }
};

/**
 * Take back the rewriting of a row: the analysis goes back to the words
 * served.
 */
const restore = (row: InterventionRow) => {
    update(row, {analysis: null});
    rewriting.value = null;
};

/**
 * Whether the posology the treatment carries is struck out: a posology
 * change is the one status that unsays it. A stop strikes the treatment
 * itself, and a replacement nothing — the product named beside the status
 * says what takes its place.
 */
const supersedes = (row: InterventionRow): boolean => statusOf(row) === 'posology_modified';

const statusLabel = (status: InterventionStatus): string => {
    return props.statuses.find(option => option.key === status)?.label ?? status;
};

/**
 * Whether the pencil has anything to open on a row: a stop is said by the
 * analysis and carries no words of its own, and a treatment left as it is
 * has no proposal to correct — deciding to change it is the status', not
 * the pencil's.
 */
const editable = (status: InterventionStatus): boolean => status !== 'stopped' && status !== 'unchanged';

/**
 * Whether a row proposes something to the doctor, as drafted.
 */
const proposes = (row: InterventionRow): boolean => {
    const draft = draftOf(row);

    return draft.status !== 'unchanged' || draft.comment.trim() !== '';
};

const proposals = computed(() => {
    if (props.frozen) {
        return props.rows.filter(row => row.intervention?.is_proposal).length;
    }

    return props.rows.filter(proposes).length + additions.value.length;
});

/**
 * A chip of a row: a finding the interview reported, read-only — the table
 * reads the findings, it never writes any.
 */
interface Chip {
    type: InterventionFindingType;
    label: string;
    source: InterventionFinding['source'];
}

/**
 * The chips of a row, family by family.
 */
const chipsOf = (row: InterventionRow): Chip[] => {
    return FAMILIES.flatMap(family => row.findings[family.type]
        .map(finding => ({type: family.type, label: finding.label, source: finding.source})));
};

/**
 * Whether a row still reads as the AI suggested it: served as suggested,
 * and drafted exactly as served. The badge goes as soon as the pharmacist
 * touches the status, the product or the posology.
 */
const isSuggested = (row: InterventionRow): boolean => {
    if (props.frozen || !row.suggested || !row.intervention) {
        return false;
    }

    const draft = draftOf(row);

    return draft.status === row.intervention.status
        && (draft.product?.id ?? null) === (row.intervention.product?.id ?? null)
        && draft.outcome === (row.intervention.outcome ?? '');
};

const update = (row: InterventionRow, patch: Partial<InterventionDraft>) => {
    emit('update:draft', row.key, {...draftOf(row), ...patch});
};

/**
 * Set the status of a row. The posology and the product only stay with a
 * status that carries them; a status that asks for one opens the row to
 * fill it.
 */
const setStatus = (row: InterventionRow, status: InterventionStatus) => {
    const draft = draftOf(row);

    update(row, {
        status,
        outcome: requiresOutcome(status) ? draft.outcome : '',
        product: namesProduct(status) ? draft.product : null
    });

    editing.value = requiresOutcome(status) || namesProduct(status)
        ? row.key
        : (editing.value === row.key ? null : editing.value);
};

/**
 * Whether the proposal of a row is being written: on the pharmacist's
 * click, or while a change still lacks its posology or its product.
 */
const isEditing = (row: InterventionRow): boolean => {
    if (props.frozen) {
        return false;
    }

    const draft = draftOf(row);

    return editing.value === row.key
        || (requiresOutcome(draft.status) && draft.outcome.trim() === '')
        || (namesProduct(draft.status) && draft.product === null);
};

/**
 * Open the proposal of a row to write it again. A replacement reopens on
 * its product, searched for again. The status is never touched: reopening
 * is for correcting the proposal, not for resetting the decision — nor for
 * taking one, which is the status' own.
 */
const edit = (row: InterventionRow) => {
    if (namesProduct(draftOf(row).status)) {
        update(row, {product: null});
    }

    editing.value = row.key;
};

/**
 * Take back the proposal of a row entirely: the treatment goes back to
 * being left as it is, and the words and the product go with it.
 */
const clear = (row: InterventionRow) => {
    update(row, {status: 'unchanged', outcome: '', product: null});
    editing.value = editing.value === row.key ? null : editing.value;
};

const closeEdit = (row: InterventionRow) => {
    if (editing.value === row.key) {
        editing.value = null;
    }
};

const isCommenting = (row: InterventionRow): boolean => {
    return commentOf(row).trim() !== '' || (!props.frozen && commenting.value.includes(row.key));
};

/**
 * Open the comment of a row, or take it back. Closing drops what was
 * written: a comment left in place is a proposal to the doctor, so the only
 * way to unsay it is to clear it.
 */
const toggleComment = (row: InterventionRow) => {
    if (isCommenting(row)) {
        commenting.value = commenting.value.filter(key => key !== row.key);

        if (commentOf(row) !== '') {
            update(row, {comment: ''});
        }

        return;
    }

    commenting.value = [...commenting.value, row.key];
};

const product = (legacy: LegacyProduct): InterventionProduct => ({id: legacy.id, label: legacy.name});

const add = () => {
    if (addition.value.product === null) {
        return;
    }

    emit('add', addition.value.product, addition.value.outcome.trim());
    addition.value = {product: null, outcome: ''};
    searchGeneration.value += 1;
};

/**
 * The placeholder of the comment: on a treatment left as it is, the comment
 * is the proposal itself.
 */
const commentPlaceholder = (status: InterventionStatus): string => {
    return status === 'unchanged'
        ? 'Surveillance ou remarque au médecin (ex. INR mensuel — AVK + nouvelle interaction)…'
        : 'Commentaire (repris dans la synthèse)…';
};
</script>

<template>
  <div
    :class="CARD_SHEET"
    class="max-w-[720px] rounded-xl overflow-hidden"
  >
    <div class="flex items-center gap-2 px-4 py-3">
      <ArrowLeftRight class="w-[18px] h-[18px] text-primary" />
      <span class="text-[13px] font-semibold">Intervention pharmaceutique</span>
      <span
        v-if="!frozen"
        class="text-[11.5px] text-muted-foreground"
      >— modifiez, arrêtez ou ajoutez des traitements</span>
      <span class="flex-1" />
      <span class="text-xs font-semibold text-primary">
        {{ proposals }} proposition{{ proposals > 1 ? 's' : '' }}
      </span>
    </div>

    <div
      v-if="profileAlerts.length > 0"
      class="flex flex-col gap-1 mx-4 mb-3 rounded-lg bg-gray-light/60 border border-slate-200 px-3 py-2"
    >
      <span class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        <ShieldAlert class="w-3.5 h-3.5 text-primary" />
        Alertes sur le profil du patient
      </span>
      <span
        v-for="alert in profileAlerts"
        :key="alert.id"
        class="text-[11px] leading-snug"
      >
        <span class="font-semibold" :class="alertLevelTone(alert.severity).text">{{ alert.title }}</span>
        <span class="text-muted-foreground"> · {{ alert.source_label }}</span>
        <span v-if="alert.content && !alert.referential" class="block text-muted-foreground">{{ alert.content }}</span>
      </span>
    </div>

    <div :class="GRID" class="py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
      <span>Traitement</span>
      <span>Analyse</span>
      <span>Statut</span>
      <span />
    </div>

    <div
      v-for="(row, index) in rows"
      :key="row.key"
      :class="GRID"
      class="py-2.5 items-start animate-message-in"
      :style="{animationDelay: `${index * 60}ms`}"
    >
      <span class="flex flex-col gap-1 min-w-0">
        <span
          class="text-xs font-semibold leading-tight"
          :class="{'line-through': statusOf(row) === 'stopped'}"
        >
          {{ row.label }}
        </span>
        <span
          class="text-[10.5px] text-muted-foreground"
          :class="{'line-through': supersedes(row)}"
        >{{
          row.before ?? (row.treatment_line_id === null ? '—' : 'Posologie non renseignée')
        }}</span>

        <span
          v-if="row.treatment_line_id !== null && chipsOf(row).length > 0"
          class="flex flex-wrap items-center gap-1 pt-0.5"
        >
          <span
            v-for="(chip, chipIndex) in chipsOf(row)"
            :key="chipIndex"
            :class="[CHIP, FINDING_CHIPS[chip.type]]"
            :title="SOURCE_TITLES[chip.source]"
          >
            {{ chip.label }}
          </span>
        </span>
      </span>

      <span class="text-[11px] leading-snug min-w-0">
        <span
          v-if="drafting"
          class="flex items-center gap-1.5 text-muted-foreground"
        >
          <Sparkles class="w-3 h-3 shrink-0 text-primary" />
          <span>L'IA relit l'analyse…</span>
          <LoaderCircle class="w-3 h-3 shrink-0 animate-spin" />
        </span>
        <span v-else-if="isRewriting(row)" class="flex flex-col gap-1">
          <Textarea
            :ref="bindAnalysisField"
            :model-value="draftOf(row).analysis ?? ''"
            class="!min-h-0 w-full resize-none overflow-hidden !border !border-solid !border-primary bg-white px-2 py-1 text-[11px] leading-snug shadow-none focus-visible:ring-0"
            :disabled="busy"
            placeholder="Analyse du traitement…"
            @update:model-value="update(row, {analysis: String($event)}); triggerResize()"
            @keydown.escape="closeRewrite(row)"
          />
          <span class="flex items-center gap-2">
            <button
              class="flex items-center gap-1 text-[10.5px] font-semibold text-secondary-darkest hover:opacity-70 disabled:opacity-40"
              :disabled="busy"
              @click="closeRewrite(row)"
            >
              <Check class="w-3.5 h-3.5" />
              Valider
            </button>
            <button
              v-if="row.analysis"
              class="text-[10.5px] font-semibold text-muted-foreground hover:text-primary disabled:opacity-40"
              :disabled="busy"
              title="Revenir à l'analyse initiale"
              @click="restore(row)"
            >
              Rétablir
            </button>
          </span>
        </span>
        <button
          v-else-if="!frozen && !analysisOf(row)"
          class="flex w-full items-center gap-1.5 rounded-md border border-dashed border-gray px-2 py-1 text-left text-[11px] text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-50"
          :disabled="busy"
          @click="rewrite(row)"
        >
          <Pencil class="w-3 h-3 shrink-0" />
          <span>Rédiger l'analyse…</span>
        </button>
        <template v-else>
          <span :class="analysisOf(row) ? 'text-gray-dark' : 'text-muted-foreground'">{{ analysisOf(row) || '—' }}</span>
          <button
            v-if="!frozen"
            class="ml-1 align-baseline text-muted-foreground hover:text-primary disabled:opacity-50"
            :disabled="busy"
            title="Modifier l’analyse"
            @click="rewrite(row)"
          >
            <Pencil class="inline w-3 h-3" />
          </button>
        </template>
      </span>

      <span class="flex flex-col gap-1 min-w-0">
        <span
          v-if="frozen || row.treatment_line_id === null"
          class="self-start px-2 py-1 rounded-full text-[10.5px] font-bold"
          :class="STATUS_CHIPS[statusOf(row)]"
        >
          {{ row.intervention?.status_label ?? statusLabel(statusOf(row)) }}
        </span>

        <Select
          v-else
          :model-value="draftOf(row).status"
          :disabled="busy"
          @update:model-value="setStatus(row, $event as InterventionStatus)"
        >
          <SelectTrigger
            class="h-7 w-full rounded-full border-0 px-2 text-[10.5px] font-bold shadow-none focus:ring-0"
            :class="STATUS_CHIPS[draftOf(row).status]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="option in statuses.filter(option => option.key !== 'added')"
              :key="option.key"
              :value="option.key"
              class="text-xs"
            >
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <span
          v-if="productOf(row) && statusOf(row) === 'replaced'"
          class="text-[10.5px] text-muted-foreground truncate"
          :title="productOf(row)!.label"
        >
          → {{ productOf(row)!.label }}
        </span>

        <span
          v-if="!isEditing(row) && outcomeOf(row).trim() !== ''"
          class="text-[10.5px] text-muted-foreground truncate"
          :title="outcomeOf(row)"
        >
          → {{ outcomeOf(row) }}
        </span>

        <MedicationCheckupAiBadge
          v-if="isSuggested(row)"
          class="self-start"
          title="Proposition de l'IA, sans source référentielle — à relire avant de valider"
        />
      </span>

      <span v-if="!frozen" class="flex justify-end gap-1.5 pt-0.5">
        <button
          class="text-primary hover:opacity-70 disabled:opacity-50"
          :disabled="busy"
          title="Commentaire"
          @click="toggleComment(row)"
        >
          <MessageSquareText v-if="commentOf(row).trim() !== ''" class="w-[15px] h-[15px]" />
          <MessageSquarePlus v-else class="w-[15px] h-[15px]" />
        </button>
        <button
          v-if="row.treatment_line_id !== null && !isEditing(row) && editable(draftOf(row).status)"
          class="text-muted-foreground hover:text-primary disabled:opacity-50"
          :disabled="busy"
          title="Modifier la proposition"
          @click="edit(row)"
        >
          <Pencil class="w-[15px] h-[15px]" />
        </button>
        <button
          v-if="row.treatment_line_id !== null && !isEditing(row) && draftOf(row).status !== 'unchanged'"
          class="text-muted-foreground hover:text-red-500 disabled:opacity-50"
          :disabled="busy"
          title="Annuler la proposition"
          @click="clear(row)"
        >
          <Trash2 class="w-[15px] h-[15px]" />
        </button>
      </span>
      <span v-else />

      <template v-if="!frozen && row.treatment_line_id !== null && isEditing(row)">
        <span v-if="namesProduct(draftOf(row).status) && draftOf(row).product === null" :class="SUBROW">
          <CirclePlus class="w-3.5 h-3.5 shrink-0 text-primary" />
          <BaseProductSearchBar
            class="flex-1"
            :disabled="busy"
            placeholder="Produit de remplacement…"
            @select="update(row, {product: product($event)}); closeEdit(row)"
          />
        </span>
        <span v-if="requiresOutcome(draftOf(row).status)" :class="SUBROW">
          <Input
            :model-value="draftOf(row).outcome"
            class="flex-1 min-w-0 h-7 !border !border-solid !border-primary bg-white px-2 py-1 text-[11.5px] shadow-none"
            :disabled="busy"
            placeholder="Nouvelle posologie (ex. 500 mg matin et soir)…"
            @update:model-value="update(row, {outcome: String($event)})"
            @keydown.enter.prevent="closeEdit(row)"
          />
          <button
            class="text-secondary-darkest hover:opacity-70 disabled:opacity-40"
            :disabled="draftOf(row).outcome.trim() === ''"
            title="Valider la proposition"
            @click="closeEdit(row)"
          >
            <Check class="w-4 h-4" />
          </button>
        </span>
      </template>

      <span v-if="isCommenting(row)" :class="SUBROW">
        <MessageSquareText class="w-3.5 h-3.5 shrink-0 text-primary" />
        <Input
          v-if="!frozen"
          :model-value="commentOf(row)"
          :class="SUBINPUT"
          :disabled="busy"
          :placeholder="commentPlaceholder(draftOf(row).status)"
          @update:model-value="update(row, {comment: String($event)})"
        />
        <span v-else class="text-[11px] italic text-muted-foreground">{{ commentOf(row) }}</span>
      </span>
    </div>

    <div
      v-for="entry in additions"
      :key="entry.key"
      :class="GRID"
      class="py-2.5 items-start animate-message-in"
    >
      <span class="flex flex-col gap-px min-w-0">
        <span class="text-xs font-semibold leading-tight">{{ entry.product.label }}</span>
        <span class="text-[10.5px] text-muted-foreground">—</span>
      </span>
      <span class="text-[11px] leading-snug min-w-0" :class="entry.analysis ? 'text-gray-dark' : 'text-muted-foreground'">
        {{ entry.analysis || '—' }}
      </span>
      <span class="flex flex-col gap-1 min-w-0">
        <span class="self-start px-2 py-1 rounded-full text-[10.5px] font-bold" :class="STATUS_CHIPS.added">
          {{ statusLabel('added') }}
        </span>
        <MedicationCheckupAiBadge
          v-if="entry.suggested"
          class="self-start"
          title="Proposition de l'IA, sans source référentielle — à relire avant de valider"
        />
      </span>
      <span class="flex justify-end gap-1.5 pt-0.5">
        <button
          class="text-muted-foreground hover:text-red-500 disabled:opacity-50"
          :disabled="busy"
          title="Retirer l'ajout"
          @click="emit('remove-addition', entry.key)"
        >
          <Trash2 class="w-[15px] h-[15px]" />
        </button>
      </span>

      <span :class="SUBROW">
        <Input
          :model-value="entry.outcome"
          class="flex-1 min-w-0 h-7 !border !border-solid px-2 py-1 text-[11.5px] shadow-none"
          :class="entry.outcome.trim() === '' ? '!border-primary bg-white' : '!border-gray bg-gray-light'"
          :disabled="busy"
          placeholder="Posologie proposée..."
          @update:model-value="emit('update:addition', {...entry, outcome: String($event)})"
        />
      </span>
      <span :class="SUBROW">
        <MessageSquareText class="w-3.5 h-3.5 shrink-0 text-primary" />
        <Input
          :model-value="entry.comment"
          :class="SUBINPUT"
          :disabled="busy"
          placeholder="Commentaire (repris dans la synthèse)…"
          @update:model-value="emit('update:addition', {...entry, comment: String($event)})"
        />
      </span>
    </div>

    <div v-if="!frozen" class="flex items-center gap-2 px-4 py-2.5 border-t border-gray-light bg-gray-light">
      <CirclePlus class="w-[17px] h-[17px] shrink-0 text-primary" />
      <span
        v-if="addition.product"
        class="flex-1 min-w-0 truncate rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary"
      >
        {{ addition.product.label }}
      </span>
      <BaseProductSearchBar
        v-else
        :key="searchGeneration"
        class="flex-1"
        :disabled="busy"
        placeholder="Nouveau traitement (ex. Calcium + vit. D3)"
        @select="addition.product = product($event)"
      />
      <Input
        v-model="addition.outcome"
        class="w-[170px] h-8 !border-solid !border-gray bg-white px-2.5 py-1.5 text-xs"
        :disabled="busy"
        placeholder="Posologie proposée"
        @keydown.enter.prevent="add"
      />
      <button
        class="rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-primary-dark hover:opacity-75 disabled:opacity-50"
        :disabled="busy || addition.product === null"
        @click="add"
      >
        Ajouter
      </button>
    </div>
  </div>
</template>
